import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { getErrorMessage } from "@/lib/errorMessage";
import { useSuspenseQuery, useQuery, useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Suspense, useEffect, useState } from "react";
import { AccountHub, type AccountView } from "@/components/account/AccountHub";
import { getMyProfile } from "@/lib/account/profile.functions";
import { getMyEmailStatus, isPlaceholderEmail } from "@/lib/account/email.functions";
import {
  getMyOrders,
  getMyIncompleteCheckouts,
  type CustomerOrder,
  type IncompleteCheckout,
} from "@/lib/customer-orders.functions";
import { getProductHeroImage } from "@/lib/productHeroImages";
import { getReviewableProducts } from "@/lib/reviews.functions";
import { getMyTruCoinsLedger, type LedgerEntry } from "@/lib/loyalty/customer-ledger.functions";
import { getMyReferralTimeline, type ReferralTimelineItem } from "@/lib/loyalty/referral-timeline.functions";
import { getMyReferralCode } from "@/lib/loyalty/referral-code.functions";
import { getMyCoupons, type MyCoupon } from "@/lib/coupons.functions";
import { signOutAndRedirect } from "@/lib/auth/sign-out";
import type { AuthedAccess } from "@/routes/_authenticated/route";
import { Button } from "@/components/ui/button";
import {
  PenLine,
  CheckCircle2,
  Package,
  Sparkles,
  ArrowUpRight,
  Coins,
  ExternalLink,
  Users,
  Repeat,
  RotateCcw,
  TicketPercent,
  Copy,
  Heart,
} from "lucide-react";
import { useWishlist } from "@/hooks/useWishlist";
import { savedProductTitle } from "@/lib/wishlist";
import { toast } from "sonner";
import type { User } from "@supabase/supabase-js";
import { useCartStore } from "@/stores/cartStore";
import { CancelOrderAction } from "@/components/account/CancelOrderAction";
import { EmailSettingsSection } from "@/components/account/EmailSettingsSection";
import { ProfileSettingsSection } from "@/components/account/ProfileSettingsSection";
import { QuizResultSection } from "@/components/account/QuizResultSection";


import { REFUND_WINDOW_DAYS } from "@/lib/support/refunds.functions";
import { getMyRequests, isOpenStatus, requestStatusLabel } from "@/lib/support/requests.functions";
import { resolveTrackingState, isSafeTrackingUrl, TRACKING_STAGES } from "@/lib/tracking-status";
import { cancelReasonText } from "@/lib/cancel-reasons";



export const Route = createFileRoute("/_authenticated/account")({
  validateSearch: (search: Record<string, unknown>): { view?: AccountView } => {
    const view = search.view;
    return view === "orders" || view === "rewards" || view === "profile" ? { view } : {};
  },
  head: () => ({ meta: [{ title: "My account · TruWe" }, { name: "description", content: "Your TruWe orders, TruCoins and account settings." }, { property: "og:title", content: "My account · TruWe" }, { property: "og:description", content: "Your TruWe orders, TruCoins and account settings." }, { property: "og:type", content: "website" }, { name: "twitter:card", content: "summary" }, { name: "robots", content: "noindex" }] }),
  errorComponent: ({ error, reset }: { error: unknown; reset: () => void }) => (
    <div className="mx-auto max-w-3xl px-4 py-12">
      <h1 className="font-display text-2xl font-semibold">Couldn't load your account</h1>
      <p className="mt-2 text-sm text-muted-foreground">{getErrorMessage(error)}</p>
      <Button className="mt-4" onClick={reset}>Retry</Button>
    </div>
  ),
  notFoundComponent: () => <div className="p-12 text-center">Not found</div>,
  component: AccountPage,
});

function initialsFrom(user: User | undefined): string {
  const raw =
    (user?.user_metadata?.full_name as string | undefined) ||
    (user?.user_metadata?.name as string | undefined) ||
    user?.email ||
    user?.phone ||
    "TW";
  const parts = raw.replace(/[^\p{L}\p{N}\s]/gu, " ").trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? "").join("") || "TW";
}

function greetingName(user: User | undefined): string {
  const full =
    (user?.user_metadata?.full_name as string | undefined) ||
    (user?.user_metadata?.name as string | undefined);
  if (full) return full.split(" ")[0];
  if (user?.email && !isPlaceholderEmail(user.email)) return user.email.split("@")[0];
  return "there";
}

function AccountPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { access, user } = Route.useRouteContext() as { access: AuthedAccess; user: User };
  const showAdminLink = !!access?.sections?.length;
  const { view = "overview" } = Route.useSearch();
  const fetchProfile = useServerFn(getMyProfile);
  const profile = useQuery({ queryKey: ["account", "profile"], queryFn: () => fetchProfile({}), staleTime: 30_000 });
  const fetchEmailStatus = useServerFn(getMyEmailStatus);
  const emailStatus = useQuery({ queryKey: ["account", "email-status"], queryFn: () => fetchEmailStatus({}), staleTime: 30_000 });
  const changeView = (next: AccountView) => void navigate({ to: "/account", search: next === "overview" ? {} : { view: next }, resetScroll: false });

  async function handleSignOut() {
    toast.success("Signed out");
    await signOutAndRedirect(queryClient);
  }

  // Retry unclaimed referral welcome bonus once per mount. Covers the email-
  // signup path where the customers row may not exist yet at signup time.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { getStoredReferralCode, clearStoredReferralCode } = await import("@/lib/referral/capture");
        const code = getStoredReferralCode();
        if (!code || cancelled) return;
        const { claimReferralSignupBonusServer } = await import("@/lib/loyalty/referral-signup.functions");
        const r = await claimReferralSignupBonusServer({ data: { referralCode: code } });
        if (cancelled) return;
        if (r.status === "credited" && r.creditedPaise > 0) {
          toast.success(`🎁 ₹${Math.round(r.creditedPaise / 100)} in TruCoins added to your wallet`);
          queryClient.invalidateQueries();
        }
        const terminal = new Set([
          "credited", "already_claimed", "phone_already_claimed", "self_referral",
          "invalid_code", "already_attributed_to_other", "no_reward_configured", "feature_disabled",
        ]);
        if (terminal.has(r.status)) clearStoredReferralCode();
      } catch {
        /* silent */
      }
    })();
    return () => { cancelled = true; };
  }, [queryClient]);

  const initials = profile.data?.name ? profile.data.name.trim().split(/\s+/).slice(0, 2).map((part) => part[0]?.toUpperCase()).join("") : initialsFrom(user);
  const first = profile.data?.name?.trim().split(/\s+/)[0] || greetingName(user);
  // WhatsApp sign-ups get a synthetic wa-…@wa.truwe.in address — never show it.
  const realEmail = user?.email && !isPlaceholderEmail(user.email) ? user.email : "";
  const phone = user?.phone ? `+${String(user.phone).replace(/^\+/, "")}` : "";
  const contact = realEmail || phone || "";


  return (
    <AccountHub
      firstName={first}
      initials={initials}
      contact={contact}
      view={view}
      onViewChange={changeView}
      onSignOut={handleSignOut}
      adminLink={showAdminLink ? <a href="https://admin.truwe.in/admin">Open admin</a> : undefined}
      overview={
        <>
          <section className="account-hub__latest" aria-labelledby="latest-order-heading">
            <div className="account-hub__section-title">
              <h2 id="latest-order-heading">Your latest order</h2>
              <button type="button" onClick={() => changeView("orders")}>View all <ArrowUpRight size={15} /></button>
            </div>
            <Suspense fallback={<OrderListSkeleton />}><OrderList compact /></Suspense>
          </section>
          <nav className="account-hub__shortcuts" aria-label="Account shortcuts">
            <Link to="/account/plans"><span>Your diet plans<small>Saved plans & routines</small></span><ArrowUpRight size={18} /></Link>
            <Link to="/account/requests"><span>Requests & returns<small>Help with an order</small></span><ArrowUpRight size={18} /></Link>
          </nav>
          <QuizResultSection />
          <ReviewableSection />
          <SavedProductsSection />
        </>
      }
      orders={
        <section aria-labelledby="orders-heading">
          <div className="account-hub__section-title"><h2 id="orders-heading">Your orders</h2><Link to="/account/requests">Requests <ArrowUpRight size={15} /></Link></div>
          <p className="account-hub__intro">Order details, delivery updates and support, together.</p>
          <Suspense fallback={<OrderListSkeleton />}><OrderList /></Suspense>
        </section>
      }
      rewards={<><TruCoinsSection /><MyCouponsSection /></>}
      profile={
        <>
          <ProfileSettingsSection allowPassword={emailStatus.data?.verified === true && emailStatus.data.placeholder === false} />
          <EmailSettingsSection />
          <nav className="account-hub__settings-links" aria-label="Account settings">
            <Link to="/account/notifications"><span>Notification preferences<small>Choose how we keep in touch</small></span><ArrowUpRight size={18} /></Link>
            <Link to="/contact"><span>Contact support<small>Help with your profile or order</small></span><ArrowUpRight size={18} /></Link>
          </nav>
        </>
      }
    />
  );
}

function couponValueText(c: MyCoupon): string {
  if (c.discountType === "flat") return `₹${Math.round(c.discountAmountPaise / 100)} off`;
  const pct = c.discountPercent % 1 === 0 ? c.discountPercent : c.discountPercent.toFixed(1);
  const cap = c.maxDiscountPaise != null && c.maxDiscountPaise > 0
    ? ` (up to ₹${Math.round(c.maxDiscountPaise / 100)})`
    : "";
  return `${pct}% off${cap}`;
}

/** Saved products — the wishlist hearts tapped on product pages. */
function SavedProductsSection() {
  const { handles, toggle, ready, isBusy } = useWishlist();
  if (!ready || handles.length === 0) return null;

  return (
    <section className="mt-10 md:mt-12" aria-labelledby="saved-heading">
      <h2 id="saved-heading" className="flex items-center gap-2 font-display text-xl font-semibold text-brand-deep md:text-2xl">
        <Heart size={18} className="text-brand" /> Saved products
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">Products you saved for later.</p>
      <ul className="mt-5 grid gap-3 sm:grid-cols-2">
        {handles.map((h) => (
          <li key={h} className="flex items-center justify-between gap-3 rounded-2xl border border-border bg-card p-4">
            <Link to="/products/$handle" params={{ handle: h }} className="min-w-0 font-medium text-brand-deep hover:underline">
              {savedProductTitle(h)}
            </Link>
            <Button variant="ghost" size="sm" disabled={isBusy(h)} onClick={() => void toggle(h)} aria-label={`Remove ${savedProductTitle(h)} from saved products`}>
              Remove
            </Button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function MyCouponsSection() {
  const fn = useServerFn(getMyCoupons);
  const { data } = useQuery({
    queryKey: ["my-coupons"],
    queryFn: () => fn(),
    staleTime: 30_000,
  });
  const [showPast, setShowPast] = useState(false);
  const coupons = (data?.ok ? data.coupons : []) as MyCoupon[];
  const activeCoupons = coupons.filter((coupon) => coupon.status === "active");
  const pastCoupons = coupons.filter((coupon) => coupon.status !== "active");
  const visibleCoupons = showPast ? coupons : activeCoupons;
  if (coupons.length === 0) return null;

  const copyCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success(`${code} copied`);
    } catch {
      toast.error("Couldn't copy — long-press the code instead");
    }
  };

  return (
    <section className="mt-10 md:mt-12" aria-labelledby="coupons-heading">
      <h2 id="coupons-heading" className="flex items-center gap-2 font-display text-xl font-semibold text-brand-deep md:text-2xl">
        <TicketPercent size={18} className="text-brand" /> My discount codes
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Codes issued to you — apply them in the cart before checkout.
      </p>
      {!activeCoupons.length && <p className="mt-3 text-sm text-muted-foreground">No active discount codes right now.</p>}
      {!!pastCoupons.length && <button type="button" className="account-hub__text-button" aria-expanded={showPast} onClick={() => setShowPast(!showPast)}>{showPast ? "Hide past codes" : `Past codes (${pastCoupons.length})`}</button>}
      <ul className="mt-5 grid gap-3 sm:grid-cols-2">
        {visibleCoupons.map((c) => {
          const inactive = c.status !== "active";
          return (
            <li
              key={c.code}
              className={`rounded-2xl border p-4 ${
                inactive
                  ? "border-border/60 bg-muted/30 opacity-70"
                  : "border-border bg-card transition hover:border-brand/40 hover:shadow-md hover:shadow-brand/5"
              }`}
            >
              <div className="flex items-center justify-between gap-3">
                <button
                  type="button"
                  disabled={inactive}
                  onClick={() => copyCode(c.code)}
                  className="inline-flex min-w-0 items-center gap-2 font-mono text-sm font-semibold tracking-wide text-brand-deep"
                  aria-label={inactive ? `${c.code} — ${c.status}` : `Copy code ${c.code}`}
                >
                  <span className="truncate">{c.code}</span>
                  <Copy size={13} className="shrink-0 text-muted-foreground" />
                </button>
                <span
                  className={`shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${
                    c.status === "active"
                      ? "bg-emerald-50 text-emerald-700"
                      : c.status === "used"
                        ? "bg-muted text-muted-foreground"
                        : "bg-amber-50 text-amber-700"
                  }`}
                >
                  {c.status === "active" ? "Active" : c.status === "used" ? "Used" : "Expired"}
                </span>
              </div>
              <p className="mt-1.5 text-sm font-medium text-brand-deep">{couponValueText(c)}</p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {c.prepaidOnly ? "Prepaid orders only · " : ""}
                {c.minSubtotalPaise != null && c.minSubtotalPaise > 0
                  ? `Min order ₹${Math.round(c.minSubtotalPaise / 100)} · `
                  : ""}
                {c.status === "active"
                  ? `Valid till ${new Date(c.expiresAt).toLocaleDateString("en-IN", { dateStyle: "medium" })}`
                  : c.status === "used" && c.usedAt
                    ? `Used on ${new Date(c.usedAt).toLocaleDateString("en-IN", { dateStyle: "medium" })}`
                    : `Expired ${new Date(c.expiresAt).toLocaleDateString("en-IN", { dateStyle: "medium" })}`}
              </p>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function OrderListSkeleton() {
  return (
    <ul className="mt-6 space-y-4" aria-hidden>
      {[0, 1].map((i) => (
        <li
          key={i}
          className="h-36 animate-pulse rounded-2xl border border-border/60 bg-card/60"
        />
      ))}
    </ul>
  );
}

function ReviewableSection() {
  const fn = useServerFn(getReviewableProducts);
  const { data, isLoading } = useQuery({
    queryKey: ["reviewable-account"],
    queryFn: () => fn(),
    staleTime: 30_000,
  });
  const items = data?.items ?? [];
  if (isLoading || items.length === 0) return null;

  return (
    <section className="mt-10 md:mt-12" aria-labelledby="reviewable-heading">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h2 id="reviewable-heading" className="flex items-center gap-2 font-display text-xl font-semibold text-brand-deep md:text-2xl">
            <Sparkles size={18} className="text-brand" /> Share your experience
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Delivered products you can review — every review earns <span className="font-medium text-brand">50 TruCoins</span>, whatever the rating. Honest feedback helps more than a high score.
          </p>

        </div>
      </div>
      <ul className="mt-5 grid gap-3 sm:grid-cols-2">
        {items.map((it: any) => (
          <li
            key={`${it.orderId}:${it.productId}`}
            className="group grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 rounded-2xl border border-border bg-card p-4 transition hover:border-brand/40 hover:shadow-md hover:shadow-brand/5"
          >
            <div className="min-w-0">
              <p className="truncate font-medium text-brand-deep">{it.title}</p>
              <p className="mt-0.5 truncate text-xs text-muted-foreground">
                Order {it.orderName} · {new Date(it.placedAt).toLocaleDateString("en-IN", { dateStyle: "medium" })}
              </p>
            </div>
            {it.alreadyReviewed ? (
              <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700">
                <CheckCircle2 size={14} /> Submitted
              </span>
            ) : (
              <Link
                to="/review"
                className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-brand px-4 py-2 text-xs font-semibold text-white shadow-sm shadow-brand/20 transition hover:bg-brand-deep hover:shadow-brand/30"
              >
                <PenLine size={14} /> Write
              </Link>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}


const STAGE_LABELS: Record<string, string> = {
  confirmed: "Confirmed",
  packing: "Packing",
  shipped: "Dispatched",
  return_in_transit: "Returning to TruWe",
  returned: "Returned to TruWe",
  unfulfilled: "Not dispatched yet",
  out_for_delivery: "Out for delivery",
  delivered: "Delivered",
};

function prettyStatus(raw: string) {
  return raw.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function isCancelledOrder(order: CustomerOrder) {
  const s = (order.status || "").toLowerCase();
  return s === "cancelled" || s === "canceled";
}

/** Replaces the shipment timeline once an order has been cancelled. */
function CancelledStrip({ order }: { order: CustomerOrder }) {
  const c = order.cancellation;
  const reason = cancelReasonText(c?.reasonCode);
  const when = c?.at
    ? new Date(c.at).toLocaleString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : null;

  return (
    <div className="border-b border-border/60 bg-rose-50/60 px-5 py-4">
      <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-rose-500">Status</p>
      <p className="mt-0.5 text-sm font-semibold text-rose-700">Order cancelled</p>
      {when && <p className="mt-0.5 text-xs text-rose-700/80">Cancelled on {when}</p>}
      <p className="mt-2 text-xs leading-relaxed text-rose-900/80">
        {reason ??
          "This order was cancelled. Contact support if you need help re-placing it."}
      </p>
    </div>
  );
}

/**
 * Live shipment summary on the order card: courier, AWB, current stage and the
 * latest carrier update. Data comes straight from the shipment record kept in
 * sync by the courier webhook, so this reflects the same timeline as the
 * tracking page without needing a click.
 */
function ShipmentStrip({ order }: { order: CustomerOrder }) {
  const s = order.shipment;
  const state = resolveTrackingState({
    orderStatus: order.status,
    shipmentStatus: s?.status ?? null,
    latestEventStatus: s?.latestEvent?.status ?? null,
    fulfillmentStatus: order.fulfillmentStatus,
  });
  const carrierUrl = isSafeTrackingUrl(s?.trackingUrl) ? s!.trackingUrl : null;

  // A cancelled order never shows a progress bar or "Confirmed".
  if (isCancelledOrder(order)) return <CancelledStrip order={order} />;

  if (!s && !order.fulfillmentStatus) return null;

  return (
    <div className="border-b border-border/60 bg-muted/20 px-5 py-4">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <div className="min-w-0">
          <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
            Shipment
          </p>
          <p className="mt-0.5 truncate text-sm font-semibold text-brand-deep">
            {state.exception ? state.exception.label : STAGE_LABELS[state.stage]}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
          <span>
            Courier:{" "}
            <span className="font-medium text-brand-deep">{s?.courier || "Assigned soon"}</span>
          </span>
          <span>
            AWB:{" "}
            <span className="font-mono font-medium text-brand-deep">{s?.awb || "—"}</span>
          </span>
          {carrierUrl && (
            <a
              href={carrierUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 font-semibold text-brand hover:text-brand-deep"
            >
              Carrier page <ExternalLink size={12} />
            </a>
          )}
        </div>
      </div>

      {/* Stage progress — mirrors the tracking page timeline */}
      <div className="mt-3 flex gap-1" aria-hidden="true">
        {TRACKING_STAGES.map((stage, i) => (
          <span
            key={stage}
            className={`h-1.5 flex-1 rounded-full ${
              state.exception && i > state.stageIndex
                ? "bg-amber-200"
                : i <= state.stageIndex
                  ? "bg-brand"
                  : "bg-border"
            }`}
          />
        ))}
      </div>
      <div className="mt-1.5 flex justify-between text-[10px] uppercase tracking-wider text-muted-foreground">
        {TRACKING_STAGES.map((stage, i) => (
          <span key={stage} className={i <= state.stageIndex ? "text-brand-deep" : ""}>
            {STAGE_LABELS[stage]}
          </span>
        ))}
      </div>

      {s?.latestEvent && (
        <p className="mt-2 text-xs text-muted-foreground">
          {prettyStatus(s.latestEvent.status)}
          {s.latestEvent.location ? ` · ${s.latestEvent.location}` : ""}
          {s.latestEvent.occurredAt
            ? ` · ${new Date(s.latestEvent.occurredAt).toLocaleString("en-IN", {
                day: "numeric",
                month: "short",
                hour: "numeric",
                minute: "2-digit",
              })}`
            : ""}
        </p>
      )}
    </div>
  );
}

function OrderList({ compact = false }: { compact?: boolean }) {

  const fetchOrders = useServerFn(getMyOrders);
  const { data, refetch, isFetching } = useSuspenseQuery({
    queryKey: ["my-orders"],
    queryFn: () => fetchOrders(),
    staleTime: 30_000,
    // Keep courier / AWB / status fresh while the page stays open.
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
  });


  if (data.error) {
    return (
      <div className="mt-6 rounded-2xl border border-amber-200/70 bg-amber-50/60 p-6 text-sm text-amber-900">
        <p>We couldn't reach the order service right now.</p>
        <Button variant="outline" className="mt-3" disabled={isFetching} onClick={() => void refetch()}>{isFetching ? "Loading…" : "Try again"}</Button>
      </div>
    );
  }
  if (!data.orders.length) {
    return (
      <>
        <div className="mt-6 rounded-3xl border border-dashed border-border bg-card/60 p-10 text-center">
          <div className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-brand-soft/60 text-brand">
            <Package size={22} />
          </div>
          <p className="mt-4 text-sm text-foreground">
            No orders on your account yet.
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            Your confirmed orders will appear here. Need help finding an order? <Link to="/contact" className="underline underline-offset-2">Contact support</Link>.
          </p>
          <Link
            to="/shop"
            className="mt-5 inline-flex items-center gap-1.5 rounded-full bg-brand px-5 py-2 text-sm font-semibold text-white transition hover:bg-brand-deep"
          >
            Explore products <ArrowUpRight size={14} />
          </Link>
        </div>
        {!compact && <IncompleteCheckoutList />}
      </>
    );
  }

  return (
    <>
      <ul className="mt-4 space-y-4">
        {(compact ? data.orders.slice(0, 1) : data.orders).map((order) => (
          <OrderRow key={order.id} order={order} />
        ))}
      </ul>
      {!compact && <IncompleteCheckoutList />}
    </>
  );
}

/**
 * Checkouts that never became a paid order (payment failed / cancelled /
 * abandoned), so the shopper can see them and finish the purchase.
 */
function IncompleteCheckoutList() {
  const fetchIncomplete = useServerFn(getMyIncompleteCheckouts);
  const { data } = useQuery({
    queryKey: ["my-incomplete-checkouts"],
    queryFn: () => fetchIncomplete(),
    staleTime: 60_000,
  });
  const checkouts = data?.checkouts ?? [];
  if (!checkouts.length) return null;

  return (
    <details className="account-hub__incomplete mt-6">
      <summary>Incomplete payments <span>{checkouts.length}</span></summary>
      <p className="account-hub__intro">These attempts did not become confirmed orders. Review current prices and availability in your cart before paying.</p>
      <div className="space-y-3">{checkouts.map((c) => (
        <IncompleteCheckoutRow key={c.reference} checkout={c} />
      ))}</div>
    </details>
  );
}

function IncompleteCheckoutRow({ checkout }: { checkout: IncompleteCheckout }) {
  const addItems = useCartStore((s) => s.addItems);
  const cartLoading = useCartStore((s) => s.isLoading);
  const date = new Date(checkout.createdAt).toLocaleDateString("en-IN", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
  const buyable = checkout.lines.filter((l) => l.variantId && l.pricePaise > 0);

  async function handleRetry() {
    if (!buyable.length) {
      toast.error("These products are no longer available.");
      return;
    }
    const ok = await addItems(
      buyable.map((l) => ({
        variantId: l.variantId,
        productHandle: (l.productUrl || "").split("/").filter(Boolean).pop() || "",
        productTitle: l.name || "TruWe product",
        imageUrl: l.imageUrl || getProductHeroImage(null, l.name || ""),
        price: { amount: (l.pricePaise / 100).toFixed(2), currencyCode: checkout.currency || "INR" },
        quantity: l.quantity,
      })),
    );
    if (!ok) {
      toast.error("Couldn't add to cart. Please try again.");
      return;
    }
    toast.success("Added to cart");
    (window as unknown as { __openCart?: () => void }).__openCart?.();
  }

  return (
    <div className="rounded-2xl border border-amber-200/70 bg-amber-50/50 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-amber-900">Payment not completed</p>
          <p className="mt-0.5 text-xs text-amber-900/70">
            Attempted {date} · Ref {checkout.reference}
          </p>
          <p className="mt-1 truncate text-xs text-amber-900/80">
            {checkout.lines.map((l) => `${l.name || "Item"} × ${l.quantity}`).join(", ")}
          </p>
        </div>
        <p className="shrink-0 text-sm font-semibold text-amber-900">
          ₹{(checkout.amountPaise / 100).toLocaleString("en-IN")}
        </p>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={handleRetry} disabled={cartLoading || !buyable.length}>
          <Repeat size={14} className="mr-1.5" /> Review in cart
        </Button>
        <span className="text-[11px] text-amber-900/70">
          If you see a debit without a confirmed order, contact support with this reference.
        </span>
      </div>
    </div>
  );
}

function OrderRow({ order }: { order: CustomerOrder }) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const detailsId = `account-order-${order.id}`;
  const date = new Date(order.processedAt).toLocaleDateString("en-IN", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
  const addItems = useCartStore((s) => s.addItems);
  const cartLoading = useCartStore((s) => s.isLoading);
  const buyable = order.lineItems.filter((li) => li.variantShopifyId && li.pricePaise > 0);
  const fulfillment = String(order.fulfillmentStatus || "").toLowerCase();
  const isDelivered = fulfillment === "delivered" || fulfillment === "fulfilled" || fulfillment === "complete";
  // Return window mirrors the server rule in refunds.functions.ts (REFUND_WINDOW_DAYS).
  const msLeft = order.deliveredAt
    ? new Date(order.deliveredAt).getTime() + REFUND_WINDOW_DAYS * 86400_000 - Date.now()
    : null;
  const returnOpen = msLeft !== null && msLeft > 0;
  const returnDaysLeft = returnOpen ? Math.max(1, Math.ceil(msLeft! / 86400_000)) : null;

  // Open refund/return request on this order (shared cache with /account/requests).
  const listRequests = useServerFn(getMyRequests);
  const { data: requestData } = useQuery({
    queryKey: ["account", "requests"],
    queryFn: () => listRequests(),
    staleTime: 20_000,
  });
  const openRefund = (requestData?.requests || []).find(
    (r) => r.kind === "refund" && r.orderId === order.id && isOpenStatus(r.status),
  );



  async function handleBuyAgain() {
    if (buyable.length === 0) {
      toast.error("These products are no longer available.");
      return;
    }
    const lines = buyable.map((li) => ({
      variantId: li.variantShopifyId!,
      productHandle: li.handle || li.title.toLowerCase().replace(/\s+/g, "-"),
      productTitle: li.title,
      imageUrl: li.image || getProductHeroImage(li.handle, li.title),
      price: {
        amount: (li.pricePaise / 100).toFixed(2),
        currencyCode: order.currency || "INR",
      },
      quantity: li.quantity,
    }));
    const ok = await addItems(lines);
    if (!ok) {
      toast.error("Couldn't add to cart. Please try again.");
      return;
    }
    toast.success("Added to cart");
    (window as unknown as { __openCart?: () => void }).__openCart?.();
  }

  return (
    <li className="group overflow-hidden rounded-2xl border border-border bg-card transition hover:border-brand/30 hover:shadow-lg hover:shadow-brand/5">
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3 border-b border-border/60 bg-gradient-to-br from-card to-brand-soft/20 p-5">
        <div className="min-w-0">
          <p className="truncate font-display text-lg font-semibold text-brand-deep">{order.name}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">Placed {date}</p>
        </div>
        <div className="shrink-0 text-right">
          <p className="font-display text-lg font-semibold tabular-nums text-brand-deep">
            {order.currency === "INR" ? "₹" : order.currency + " "}
            {Number(order.totalPrice).toLocaleString("en-IN")}
          </p>
          <div className="mt-1 flex flex-wrap justify-end gap-1.5 text-[10px] font-medium uppercase tracking-widest">
            {isCancelledOrder(order) ? (
              <span className="rounded-full bg-rose-100 px-2 py-0.5 text-rose-700">Cancelled</span>
            ) : (
              <>
                {order.financialStatus && (
                  <span className="rounded-full bg-brand-soft/70 px-2 py-0.5 text-brand-deep">
                    {order.financialStatus}
                  </span>
                )}
                {order.fulfillmentStatus && (
                  <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-emerald-700">
                    {prettyStatus(order.fulfillmentStatus)}
                  </span>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {!detailsOpen && <p className="px-5 py-3 text-sm text-muted-foreground">{order.lineItems.map((line) => `${line.title} × ${line.quantity}`).join(" · ")}</p>}
      <div id={detailsId} hidden={!detailsOpen}>
      <ShipmentStrip order={order} />
      <ul className="divide-y divide-border/60">
        {order.lineItems.map((li, i) => {
          const src = li.image || getProductHeroImage(li.handle, li.title);
          return (
          <li key={i} className="flex items-center gap-3 px-5 py-3 text-sm">
            {src ? (
              <img
                src={src}
                alt={li.title}
                loading="lazy"
                className="h-12 w-12 shrink-0 rounded-xl border border-border/60 bg-white object-contain"
              />
            ) : (
              <div className="h-12 w-12 shrink-0 rounded-xl bg-muted" />
            )}
            <div className="min-w-0 flex-1">
              {li.handle ? (
                <Link
                  to="/products/$handle"
                  params={{ handle: li.handle }}
                  className="truncate font-medium text-brand-deep transition hover:text-brand"
                >
                  {li.title}
                </Link>
              ) : (
                <p className="truncate font-medium text-brand-deep">{li.title}</p>
              )}
              {li.variantTitle && <p className="truncate text-xs text-muted-foreground">{li.variantTitle}</p>}
            </div>
            <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
              × {li.quantity}
            </span>
          </li>
          );
        })}
      </ul>

      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border/60 bg-muted/30 px-5 py-3">
        <button type="button" aria-expanded={detailsOpen} aria-controls={detailsId} onClick={() => setDetailsOpen(!detailsOpen)} className="account-hub__order-toggle">
          {detailsOpen ? "Hide details" : isCancelledOrder(order) ? "View order details" : "Details & tracking"} <ArrowUpRight size={15} />
        </button>
        <div className="flex flex-wrap items-center gap-2">
          <CancelOrderAction
            orderId={order.id}
            orderName={order.name}
            financialStatus={order.financialStatus}
            fulfillmentStatus={order.fulfillmentStatus}
          />
          {openRefund && (
            <Link
              to="/account/requests"
              className="inline-flex items-center gap-1.5 rounded-full bg-brand-soft/70 px-3 py-1 text-xs font-medium text-brand-deep hover:bg-brand-soft"
            >
              Return / refund · {requestStatusLabel(openRefund.status)}
            </Link>
          )}
          {isDelivered && !openRefund &&
            (returnOpen ? (
              <Link
                to="/account/refunds"
                search={{ order: order.id }}
                className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3.5 py-1.5 text-sm font-medium text-brand-deep transition hover:border-brand hover:text-brand"
              >
                <RotateCcw size={14} /> Return / refund
                {returnDaysLeft !== null && (
                  <span className="text-xs font-normal text-muted-foreground">
                    · {returnDaysLeft} day{returnDaysLeft === 1 ? "" : "s"} left
                  </span>
                )}
              </Link>
            ) : (
              <div className="flex flex-col items-start">
                <button
                  type="button"
                  disabled
                  aria-disabled="true"
                  className="inline-flex cursor-not-allowed items-center gap-1.5 rounded-full border border-border bg-muted px-3.5 py-1.5 text-sm font-medium text-muted-foreground opacity-70"
                >
                  <RotateCcw size={14} /> Return / refund
                </button>
                <span className="mt-1 text-[11px] text-muted-foreground">Return period over</span>
              </div>
            ))}
          {isDelivered && (
            <Button
              size="sm"
              className="rounded-full gap-1.5"
              onClick={handleBuyAgain}
              disabled={cartLoading || buyable.length === 0}
            >
              <Repeat size={14} /> Buy again
            </Button>
          )}
        </div>

      </div>

    </li>
  );
}

// -------------------- TruCoins section --------------------
function formatCoins(paise: number): string {
  const rupees = Math.round(paise) / 100;
  return `₹${rupees.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

function StatCard({ label, value, hint, accent }: { label: string; value: string; hint?: string; accent?: boolean }) {
  return (
    <div
      className={
        accent
          ? "rounded-2xl border border-brand/30 bg-gradient-to-br from-brand to-brand-deep p-4 text-white shadow-lg shadow-brand/20"
          : "rounded-2xl border border-border bg-card p-4 transition hover:border-brand/30"
      }
    >
      <p
        className={
          accent
            ? "text-[11px] font-medium uppercase tracking-[0.18em] text-white/70"
            : "text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground"
        }
      >
        {label}
      </p>
      <p
        className={
          accent
            ? "mt-1.5 font-display text-2xl font-semibold tabular-nums"
            : "mt-1.5 font-display text-xl font-semibold tabular-nums text-brand-deep"
        }
      >
        {value}
      </p>
      {hint && (
        <p className={accent ? "mt-1 text-xs text-white/75" : "mt-1 text-xs text-muted-foreground"}>{hint}</p>
      )}
    </div>
  );
}

function LedgerRow({ entry }: { entry: LedgerEntry }) {
  const signed = entry.amountPaise >= 0 ? `+${formatCoins(entry.amountPaise)}` : `−${formatCoins(Math.abs(entry.amountPaise))}`;
  const positive = entry.amountPaise > 0;
  return (
    <li className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 rounded-xl border border-border bg-card px-4 py-3 transition hover:border-brand/30">
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-brand-deep">{entry.description}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {new Date(entry.createdAt).toLocaleDateString("en-IN", { dateStyle: "medium" })}
          {" · "}
          <span className="uppercase tracking-widest">{entry.status}</span>
        </p>
      </div>
      <span
        className={`shrink-0 font-display text-sm font-semibold tabular-nums ${
          positive ? "text-emerald-700" : "text-muted-foreground"
        }`}
        aria-label={`${positive ? "credit" : "debit"} of ${formatCoins(Math.abs(entry.amountPaise))}`}
      >
        {signed}
      </span>
    </li>
  );
}

function TruCoinsHeader({ balancePaise }: { balancePaise?: number }) {
  return (
    <div className="account-hub__wallet">
      <div><p className="account-hub__eyebrow">TRUCOINS</p><h2 id="trucoins-heading">Your rewards</h2><p className="account-hub__wallet-value">{typeof balancePaise === "number" ? formatCoins(balancePaise) : "—"}</p><p className="account-hub__intro">Available to use</p></div>
      <Coins size={30} strokeWidth={1.5} aria-hidden="true" />
    </div>
  );
}

function ReferAndEarnSection() {
  return (
    <section className="mt-10 md:mt-12" aria-labelledby="refer-earn-heading">
      <Link
        to="/referral"
        className="group flex w-full items-center justify-between gap-4 rounded-2xl border border-border/70 bg-card px-6 py-5 text-left shadow-sm transition hover:border-amber-400/60 hover:bg-amber-50/40 sm:px-8"
        aria-controls="refer-earn-panel"
      >
        <div className="flex items-center gap-4">
          <div className="grid h-11 w-11 place-items-center rounded-full bg-amber-100 text-amber-600">
            <Users size={22} strokeWidth={1.75} />
          </div>
          <div>
            <h2 id="refer-earn-heading" className="font-display text-lg font-semibold tracking-tight text-brand-deep sm:text-xl">
              Refer & Earn
            </h2>
            <p className="mt-0.5 text-xs text-muted-foreground sm:text-sm">
              Give ₹100 off · Get TruCoins
            </p>
          </div>
        </div>
        <span className="text-sm font-semibold text-brand transition group-hover:translate-x-0.5">
          Open →
        </span>
      </Link>
    </section>
  );
}



function TruCoinsSection() {
  const fetchLedger = useServerFn(getMyTruCoinsLedger);
  const { data, isLoading, error, refetch, fetchNextPage, isFetching } = usePagedLedger(fetchLedger);

  if (data && data.pages[0] && data.pages[0].visible === false) return null;

  if (isLoading) {
    return (
      <section className="mt-10 md:mt-12" aria-labelledby="trucoins-heading">
        <TruCoinsHeader />
        <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4" aria-hidden>
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-20 animate-pulse rounded-2xl border border-border/60 bg-card/60" />
          ))}
        </div>
      </section>
    );
  }

  if (error || !data) {
    return (
      <section className="mt-10 md:mt-12" aria-labelledby="trucoins-heading">
        <TruCoinsHeader />
        <div className="mt-4 flex flex-wrap items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <span className="min-w-0 flex-1">
            Couldn't load your TruCoins right now.
            {error?.message ? <span className="ml-1 text-xs opacity-80">({error.message})</span> : null}
          </span>
          <Button variant="outline" size="sm" onClick={() => refetch()}>Retry</Button>
        </div>
      </section>
    );
  }

  const first = data.pages[0];
  const entries = data.pages.flatMap((p) => p.entries);
  const loaded = entries.length;
  const total = first.total;
  const summary = first.summary;

  if (!summary) {
    return (
      <section className="mt-10 md:mt-12" aria-labelledby="trucoins-heading">
        <TruCoinsHeader />
        <div className="mt-4 rounded-2xl border border-dashed border-border bg-card/60 p-6 text-sm text-muted-foreground">
          No TruCoins account yet — place a prepaid order to start earning.
        </div>
      </section>
    );
  }

  return (
    <section className="mt-10 md:mt-12" aria-labelledby="trucoins-heading">
      <TruCoinsHeader balancePaise={summary.availablePaise} />



      <div className="account-hub__wallet-stats mt-4 grid grid-cols-3 gap-2">
        <StatCard label="Pending" value={formatCoins(summary.pendingPaise)} hint={first.pendingCopy} />
        {summary.reservedPaise > 0 && (
          <StatCard label="Reserved" value={formatCoins(summary.reservedPaise)} hint="Held for a pending checkout." />
        )}
        <StatCard label="Lifetime earned" value={formatCoins(summary.lifetimeEarnedPaise)} />
        <StatCard label="Lifetime redeemed" value={formatCoins(summary.lifetimeRedeemedPaise)} />
      </div>


      {first.referralProgress && <ReferralTimelineSection />}

      <h3 className="mt-6 font-display text-base font-semibold text-brand-deep">Activity</h3>
      {entries.length === 0 ? (
        <p className="mt-2 text-sm text-muted-foreground">No TruCoins activity yet.</p>
      ) : (
        <>
          <ul className="mt-3 space-y-2">
            {entries.map((e) => (
              <LedgerRow key={e.id} entry={e} />
            ))}
          </ul>
          {loaded < total && (
            <div className="mt-4 flex justify-center">
              <Button variant="outline" size="sm" className="rounded-full" onClick={() => fetchNextPage()} disabled={isFetching}>
                {isFetching ? "Loading…" : `Show more (${total - loaded} remaining)`}
              </Button>
            </div>
          )}
        </>
      )}
    </section>
  );
}

// -------------------- Referral timeline --------------------

function ReferralTimelineSection() {
  const fetchTimeline = useServerFn(getMyReferralTimeline);
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["referral-timeline"],
    queryFn: () => fetchTimeline(),
    staleTime: 30_000,
  });

  if (isLoading) {
    return (
      <div className="mt-8">
        <h3 className="font-display text-base font-semibold text-brand-deep">Your referrals</h3>
        <div className="mt-3 h-24 animate-pulse rounded-2xl border border-border/60 bg-card/60" aria-hidden />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-8 flex items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        <span className="min-w-0 flex-1">Couldn't load your referrals.</span>
        <Button variant="outline" size="sm" onClick={() => refetch()}>Retry</Button>
      </div>
    );
  }

  const items = data?.items ?? [];

  return (
    <div className="mt-8">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="font-display text-base font-semibold text-brand-deep">Your referrals</h3>
        <span className="text-xs text-muted-foreground">{items.length} total</span>
      </div>

      {items.length === 0 ? (
        <p className="mt-3 rounded-2xl border border-dashed border-border bg-card/60 p-6 text-center text-sm text-muted-foreground">
          No one has signed up with your code yet. Share your link to start earning.
        </p>
      ) : (
        <ul className="mt-3 space-y-3">
          {items.map((item) => (
            <ReferralTimelineCard key={item.id} item={item} />
          ))}
        </ul>
      )}
    </div>
  );
}

function formatTimelineDate(iso: string | null): string {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

function statusBadge(item: ReferralTimelineItem): { label: string; className: string } {
  if (item.status === "reversed" || item.reversedAt) {
    return { label: "Reversed", className: "bg-rose-50 text-rose-700 border-rose-200" };
  }
  if (item.status === "rejected") {
    return { label: "Not eligible", className: "bg-muted text-muted-foreground border-border" };
  }
  if (item.status === "pending_review") {
    return { label: "Under review", className: "bg-amber-50 text-amber-800 border-amber-200" };
  }
  if (item.creditedAt) return { label: "Credited", className: "bg-emerald-50 text-emerald-700 border-emerald-200" };
  if (item.firstOrderAt) return { label: "Order placed", className: "bg-brand-soft/60 text-brand-deep border-brand/30" };
  if (item.signedUpAt) return { label: "Signed up", className: "bg-amber-50 text-amber-800 border-amber-200" };
  return { label: "Invited", className: "bg-muted text-muted-foreground border-border" };
}

function ReferralTimelineCard({ item }: { item: ReferralTimelineItem }) {
  const badge = statusBadge(item);
  const isReversed = item.status === "reversed" || !!item.reversedAt;

  const steps: Array<{ key: string; label: string; at: string | null; done: boolean }> = [
    { key: "invited", label: "Invited", at: item.invitedAt, done: !!item.invitedAt },
    { key: "signedUp", label: "Signed up", at: item.signedUpAt, done: !!item.signedUpAt },
    { key: "firstOrder", label: "Placed first order", at: item.firstOrderAt, done: !!item.firstOrderAt },
    {
      key: "credited",
      label: item.rewardPaise > 0
        ? `Credited ${formatCoins(item.rewardPaise)}`
        : "Credited",
      at: item.creditedAt,
      done: !!item.creditedAt,
    },
  ];

  return (
    <li className="rounded-2xl border border-border/70 bg-card p-4 shadow-sm sm:p-5">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-medium text-brand-deep">
            {item.displayName}
            {item.displayHint && (
              <span className="ml-2 text-xs font-normal text-muted-foreground">{item.displayHint}</span>
            )}
          </p>
          {item.referralCode && (
            <p className="mt-0.5 text-[11px] uppercase tracking-wider text-muted-foreground">
              via {item.referralCode}
            </p>
          )}
        </div>
        <span className={`rounded-full border px-2.5 py-1 text-[11px] font-medium ${badge.className}`}>
          {badge.label}
        </span>
      </div>

      <ol className="mt-4 space-y-3" aria-label="Referral progress">
        {steps.map((step, idx) => {
          const isLast = idx === steps.length - 1;
          const dim = isReversed && step.key === "credited";
          return (
            <li key={step.key} className="relative flex gap-3">
              <div className="flex flex-col items-center">
                <span
                  className={`grid h-6 w-6 shrink-0 place-items-center rounded-full border text-[10px] ${
                    step.done && !dim
                      ? "border-emerald-500 bg-emerald-500 text-white"
                      : "border-border bg-card text-muted-foreground"
                  }`}
                  aria-hidden
                >
                  {step.done && !dim ? <CheckCircle2 size={12} /> : idx + 1}
                </span>
                {!isLast && (
                  <span
                    className={`mt-1 w-px flex-1 ${step.done && !dim ? "bg-emerald-500/60" : "bg-border"}`}
                    aria-hidden
                  />
                )}
              </div>
              <div className="min-w-0 pb-1">
                <p className={`text-sm ${step.done && !dim ? "text-brand-deep" : "text-muted-foreground"}`}>
                  {step.label}
                </p>
                {step.at && (
                  <p className="text-[11px] text-muted-foreground">{formatTimelineDate(step.at)}</p>
                )}
                {!step.at && !step.done && step.key === "firstOrder" && item.signedUpAt && (
                  <p className="text-[11px] text-muted-foreground">Waiting for their first paid order</p>
                )}
                {!step.at && !step.done && step.key === "credited" && item.firstOrderAt && (
                  <p className="text-[11px] text-muted-foreground">TruCoins credit in progress</p>
                )}
              </div>
            </li>
          );
        })}
      </ol>

      {isReversed && (
        <p className="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-[11px] text-rose-800">
          Reward reversed{item.reviewReason ? ` — ${item.reviewReason}` : ""}
          {item.reversedAt ? ` on ${formatTimelineDate(item.reversedAt)}` : ""}.
        </p>
      )}
      {item.status === "pending_review" && (
        <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-[11px] text-amber-900">
          Under review by our team{item.reviewReason ? ` — ${item.reviewReason}` : ""}.
        </p>
      )}
    </li>
  );
}


type LedgerPageShape = Awaited<ReturnType<typeof getMyTruCoinsLedger>>;

function usePagedLedger(fetchLedger: (args: { data: { limit: number; offset: number } }) => Promise<LedgerPageShape>) {
  const PAGE = 20;
  return useInfiniteQuery<LedgerPageShape, Error>({
    queryKey: ["trucoins-ledger"],
    queryFn: ({ pageParam = 0 }) =>
      fetchLedger({ data: { limit: PAGE, offset: pageParam as number } }),
    initialPageParam: 0,
    getNextPageParam: (last: LedgerPageShape, all: LedgerPageShape[]) => {
      const loaded = all.reduce((n: number, p: LedgerPageShape) => n + (p?.entries?.length ?? 0), 0);
      return loaded < (last?.total ?? 0) ? loaded : undefined;
    },
    staleTime: 30_000,
  });
}

